// One answered Talent SELECTION, and the wait when there isn't one yet.
//
// The page asks for a selection. The server answers `ready` from its result store
// when someone has already asked that question inside the cache window, and
// `pending` when it has to go to the warehouse. Either way, if the stored answer
// was old the server also enqueued a fresher one. Both cases land the same way: an
// event on the employee's own channel naming the key, and THEN we read it — once,
// by key.
//
// THIS IS NOT POLLING (doctrine 1.2), and the shape is what makes that true: there
// is no timer anywhere in this file. The only things that trigger a read are an
// inbound event naming the key we hold, and a socket-level reconnect. It replaced
// the page's old bounded settle-read (two one-shot refetches at 15s/45s) — that
// existed only because the snapshot had no completion signal; this one does.
//
// THE DECISIONS LIVE IN `talent-result-state.ts`, NOT HERE. Review found six ways
// this path could strand a reader on a spinner, and every one was a decision —
// which event applies, when a cursor is stale, what status to report — that no test
// in this repo could reach through a hook (this app depends on no renderer). They
// are pure functions now, enumerated in `talent-result-state.test.ts`. What is left
// here is plumbing: subscribe, read, hold. Change a RULE there, not here.
import { usePusherChannel } from "@recruiter/shared";
import type { TalentQueryResult, TalentQuerySelection } from "@recruiter/shared/types";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchTalentResult } from "./api";
import { employeePusherConfig, talentChannelFor } from "./pusher";
import { meQuery, talentMetricsQuery } from "./queries";
import {
  NO_CURSOR,
  pickEvent,
  resultView,
  type AppliedCursor,
  type TalentResultReadyEvent,
} from "./talent-result-state";

export interface TalentResultState {
  result: TalentQueryResult | null;
  /** TRUE while the warehouse is answering and we have nothing to show. The page
   *  shows a spinner rather than the PREVIOUS selection's numbers: rendering old
   *  rows under new filters is how a reader screenshots the wrong thing. */
  isPending: boolean;
  /** The query failed, could not be queued, or the answer aged out. */
  error: string | null;
  /** The server served an answer and enqueued a fresher one behind it. Purely
   *  informational; the update arrives on its own. */
  refreshing: boolean;
  isLoading: boolean;
  isError: boolean;
  errorObject: unknown;
}

export function useTalentResult(selection: TalentQuerySelection): TalentResultState {
  const me = useQuery(meQuery);
  const query = useQuery(talentMetricsQuery(selection));
  const response = query.data;

  // The key we currently hold. Set whether the answer is ready or pending, because
  // a READY-but-stale answer also has a rebuild coming, and we want that to land in
  // place rather than needing a timer to notice it.
  const activeKey = response?.resultKey ?? "";
  const email = me.data?.email ?? null;

  const [late, setLate] = useState<{
    key: string;
    result: TalentQueryResult | null;
    error: string | null;
  }>({ key: "", result: null, error: null });

  // Set when the socket told us something happened while we had no key to read.
  // `readNow` can only act on a key we hold, and the subscribe attempt (and its
  // auth 403) happens on mount — BEFORE the POST has answered and given us one.
  // Without this the reconcile silently no-ops, the subscription is dead, no event
  // will ever arrive, and the page waits forever: the exact failure the reconcile
  // was added to prevent, one step earlier.
  const missedWhileKeyless = useRef(false);

  // Read the key we hold, once, right now. The ONE recovery path here, and the
  // reason it is still not polling: it fires on a socket event, never on a clock.
  const readNow = useCallback(() => {
    if (!activeKey) {
      missedWhileKeyless.current = true;
      return;
    }
    void fetchTalentResult(activeKey)
      .then((r) => {
        if (r.result) setLate({ key: activeKey, result: r.result, error: null });
      })
      .catch(() => {
        /* A failed reconcile leaves the existing state; the next event still applies. */
      });
  }, [activeKey]);

  // Subscribed on IDENTITY, not on having a pending question: the channel is the
  // employee's and it is the same one for every selection they will ever ask, so
  // there is nothing to gain by binding it late and a race to lose — an answer can
  // land before a subscription created off the request has bound.
  //
  // `onReconnect` / `onSubscriptionError` close the other hole. With no timer here,
  // an event published while the socket was down is simply gone and the answer then
  // sits in the result store with the page waiting on it; both callbacks reconcile
  // by reading the key we already hold.
  const events = usePusherChannel<TalentResultReadyEvent>(
    email ? talentChannelFor(email) : null,
    ["talent.result.ready"],
    // The EMPLOYEE config: this channel is signed by `/api/admin/pusher/auth`.
    // `adminPusherConfig` points at the support route, which signs `private-buyer-*`
    // only, so sending it there 403s the subscription and hangs every wait here.
    employeePusherConfig,
    readNow,
    readNow,
  );

  // ...and act on it the moment a key arrives.
  useEffect(() => {
    if (!activeKey || !missedWhileKeyless.current) return;
    missedWhileKeyless.current = false;
    readNow();
  }, [activeKey, readNow]);

  const applied = useRef<AppliedCursor>(NO_CURSOR);
  useEffect(() => {
    const decision = pickEvent(events, activeKey, applied.current);
    applied.current = decision.cursor;
    if (!decision.act) return;

    const { event } = decision;
    if (!event.ok) {
      setLate({ key: event.result_key, result: null, error: event.detail || "The query failed." });
      return;
    }
    // Read ONCE, by key. Deliberately not a refetch of the query itself: re-POSTing
    // the selection would enqueue a second job for an answer that has just landed.
    void fetchTalentResult(event.result_key)
      .then((r) =>
        setLate({
          key: event.result_key,
          result: r.result ?? null,
          error: r.result ? null : r.detail || "That result is no longer available.",
        }),
      )
      .catch(() =>
        setLate({ key: event.result_key, result: null, error: "Could not read the result." }),
      );
  }, [events, activeKey]);

  const view = resultView(response, late, activeKey);
  return {
    ...view,
    isLoading: query.isLoading,
    isError: query.isError,
    errorObject: query.error,
  };
}
